import type { AppRouter } from "@l_learn/server";
import { QueryCache, QueryClient, type QueryClientConfig } from "@tanstack/react-query";
import { TRPCClientError, createTRPCClient, httpBatchLink } from "@trpc/client";
import type { inferRouterOutputs } from "@trpc/server";
import { createTRPCOptionsProxy } from "@trpc/tanstack-react-query";
import superjson from "superjson";
import { env } from "./env.mjs";

export const queryClientConfig: QueryClientConfig = {
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: false,
			retry: 0,
			refetchInterval: 4000
		},
		mutations: {
			retry: 0,
			onError: (error) => {
				console.error("Mutation error:", error);
			}
		},
		hydrate: {
			deserializeData: superjson.deserialize
		}
	},
	queryCache: new QueryCache({
		onError: (error, query) => {
			console.error("Query error:", {
				error,
				queryKey: query.queryKey,
				message: error instanceof Error ? error.message : String(error)
			});
		}
	})
};

const queryClient = new QueryClient(queryClientConfig);

export const trpcClient = createTRPCClient<AppRouter>({
	links: [
		httpBatchLink({
			url: env.NEXT_PUBLIC_SERVER_URL,
			transformer: superjson,
			fetch(url, options) {
				return fetch(url, {
					...options,
					credentials: "include"
				});
			}
		})
	]
});

export const trpc = createTRPCOptionsProxy<AppRouter>({
	client: trpcClient,
	queryClient
});

/**
 * Enum containing all api query paths
 */
export type TQuery = keyof AppRouter["_def"]["procedures"];
/**
 * This is a helper method to infer the output of a query resolver
 * @example type HelloOutput = InferQueryOutput<'hello'>
 */
export type InferSpecificQuery<TRouteKey extends TQuery> = inferRouterOutputs<AppRouter>[TRouteKey];

/**
 * Server-side utilities for SSR/SSG
 */

/**
 * Creates a query client for server-side rendering
 */
export const createServerQueryClient = () =>
	new QueryClient({
		defaultOptions: {
			dehydrate: {
				serializeData: superjson.serialize
			}
		}
	});

/**
 * Helper to prefetch queries using tRPC caller and populate query cache
 */
export const prefetchWithCaller = async <T>(
	queryClient: QueryClient,
	queryKey: readonly unknown[],
	callerFn: () => Promise<T>
) => {
	try {
		const data = await callerFn();
		queryClient.setQueryData(queryKey, data);
		return data;
	} catch (error) {
		console.error("Error prefetching query:", error);
		throw error;
	}
};

/**
 * Batch prefetch multiple queries using tRPC caller
 */
export const batchPrefetchWithCaller = async (
	queryClient: QueryClient,
	prefetches: Array<{
		queryKey: readonly unknown[];
		callerFn: () => Promise<unknown>;
	}>
) => {
	const results = await Promise.allSettled(
		prefetches.map(({ queryKey, callerFn }) =>
			prefetchWithCaller(queryClient, queryKey, callerFn)
		)
	);

	return results;
};
